import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, CheckCircle2, XCircle, AlertTriangle, ShieldCheck, Loader2, Clock, PackageX } from "lucide-react";

// Standalone Razorpay payment page — reachable independent of the chat-flow
// on BOTH this storefront's subdomain and its custom domain, as
// /pay/:pendingId. Backed by the public, no-login /public/pay/:pendingId
// endpoints (see PublicPayController + ChatflowEngineService's web
// "Pay Online" methods) — same "the uuid is the only guard" pattern as
// every other public link in this app.
//
// `:pendingId` is a PendingRazorpayOrder id, NOT a seller-portal Order id —
// mirroring the WhatsApp QR flow, no real Order exists until payment is
// actually confirmed (see ChatflowEngineService.completeWebRazorpayPayment),
// so there's no order number to show here, only the frozen cart/total.
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:9000";
const THEME_COLOR = "#520400";

const RAZORPAY_SCRIPT_ID = "razorpay-checkout-js";
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.getElementById(RAZORPAY_SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const script = document.createElement("script");
    script.id = RAZORPAY_SCRIPT_ID;
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

const resolveUrl = (path) => (path ? (path.startsWith("http") ? path : `${API_BASE}${path}`) : null);

// Mirrors PAYMENT_STATUS_BADGE in OrderDetailsPage.jsx (the seller portal's
// own Order Details view) so a given checkout's status always looks the
// same to the seller and to the customer paying it.
const PAYMENT_STATUS_META = {
  paid: { label: "Paid", cls: "bg-green-100 text-green-700", Icon: CheckCircle2 },
  pending: { label: "Pending", cls: "bg-amber-100 text-amber-800", Icon: Clock },
  failed: { label: "Failed", cls: "bg-red-100 text-red-700", Icon: XCircle },
};

// Translates a runWebRazorpayPreChecks() failure into copy for the page.
function describeCheckFailure(check) {
  switch (check?.reason) {
    case "fee_changed":
      return { type: "fee_changed", previousFare: check.previousFare, newFare: check.newFare, newTotal: check.newTotal };
    case "out_of_stock": {
      const names = check.unavailableNames || [];
      return {
        type: "error",
        message: `Sorry, ${names.join(", ") || "an item in your order"} ${names.length > 1 ? "are" : "is"} no longer available.`,
      };
    }
    case "delivery_paused":
      return { type: "error", message: "Online ordering is temporarily paused for this store. Please contact your seller." };
    case "delivery_hours_closed":
      return { type: "error", message: check.message || "We're currently outside delivery hours for this store." };
    case "expired":
      return { type: "error", message: "This payment link has expired. Please ask your seller for a new one." };
    case "already_processed":
      return { type: "error", message: "This order has already been processed." };
    default:
      return { type: "error", message: "This payment link isn't valid." };
  }
}

export default function PayPage() {
  const { pendingId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [paying, setPaying] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [payError, setPayError] = useState(null);
  const [feePrompt, setFeePrompt] = useState(null);
  const [result, setResult] = useState(null);
  const razorpayRef = useRef(null);

  const load = useCallback(() => {
    if (!pendingId) return;
    setIsLoading(true);
    setNotFound(false);
    axios
      .get(`${API_BASE}/public/pay/${pendingId}`)
      .then(({ data: res }) => setData(res.data ?? res))
      .catch(() => setNotFound(true))
      .finally(() => setIsLoading(false));
  }, [pendingId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadRazorpayScript();
    return () => {
      razorpayRef.current?.close?.();
    };
  }, []);

  const confirmPayment = useCallback(
    async (razorpayOrderId, razorpayPaymentId, signature) => {
      setPaying(false);
      setProcessing(true);
      try {
        const { data: res } = await axios.post(`${API_BASE}/public/pay/${pendingId}/confirm`, {
          razorpayOrderId,
          razorpayPaymentId,
          signature,
        });
        setResult(res?.data && res.success ? res.data : { outcome: "internal_error" });
      } catch {
        setResult({ outcome: "internal_error" });
      } finally {
        setProcessing(false);
      }
    },
    [pendingId]
  );

  const openRazorpay = useCallback(
    (rp) => {
      const razorpay = new window.Razorpay({
        key: rp.keyId,
        amount: rp.amountPaise,
        currency: rp.currency,
        order_id: rp.razorpayOrderId,
        name: data?.storeName || "Order Payment",
        description: "Order Payment",
        image: resolveUrl(data?.storeLogo) || undefined,
        // Locked read-only so Razorpay's own "Contact" confirmation screen
        // (1-Click Checkout) can't be edited into a different customer.
        prefill: {
          name: data?.customerName || "",
          contact: data?.customerPhone || "",
          email: data?.customerEmail || "",
        },
        readonly: {
          contact: !!data?.customerPhone,
          email: !!data?.customerEmail,
        },
        theme: { color: THEME_COLOR },
        handler: function (response) {
          confirmPayment(rp.razorpayOrderId, response.razorpay_payment_id, response.razorpay_signature);
        },
        modal: {
          ondismiss: function () {
            setPaying(false);
          },
        },
      });
      razorpay.on("payment.failed", function (resp) {
        setPayError(resp?.error?.description || "Payment failed. Please try again.");
        setPaying(false);
      });
      razorpayRef.current = razorpay;
      razorpay.open();
    },
    [data, confirmPayment]
  );

  const handlePay = useCallback(async () => {
    if (!data || paying) return;
    setPaying(true);
    setPayError(null);
    setFeePrompt(null);
    try {
      const ready = await loadRazorpayScript();
      if (!ready || !window.Razorpay) {
        setPayError("Payment could not be loaded. Please check your connection and try again.");
        setPaying(false);
        return;
      }

      const { data: res } = await axios.post(`${API_BASE}/public/pay/${pendingId}/razorpay-order`);
      const body = res?.data;
      if (!res?.success || !body?.ok) {
        const desc = describeCheckFailure(body?.check);
        if (desc.type === "fee_changed") {
          setFeePrompt(desc);
        } else {
          setPayError(desc.message);
        }
        setPaying(false);
        return;
      }
      openRazorpay(body);
    } catch (err) {
      setPayError(err?.response?.data?.message || "Could not start payment. Please try again.");
      setPaying(false);
    }
  }, [data, pendingId, paying, openRazorpay]);

  const Header = ({ storeName, storeLogo }) => (
    <div className="flex items-center gap-3 border-b border-slate-200 bg-white px-3 py-2.5 shrink-0">
      <button
        type="button"
        onClick={() => navigate("/")}
        aria-label="Back"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#520400] text-white shadow-sm transition-transform active:scale-95"
      >
        <ArrowLeft className="h-5 w-5" />
      </button>
      {storeLogo ? (
        <img src={resolveUrl(storeLogo)} alt={storeName || "Store"} className="h-10 w-auto shrink-0 object-contain" />
      ) : storeName ? (
        <span className="truncate text-base font-bold text-slate-900">{storeName}</span>
      ) : null}
      <div className="ml-auto flex items-center gap-1.5 rounded-lg border border-emerald-600 bg-emerald-50 px-2 py-1">
        <span className="flex h-3.5 w-3.5 items-center justify-center rounded-[3px] border-2 border-emerald-600">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" />
        </span>
        <span className="text-[10px] font-extrabold uppercase tracking-wide text-emerald-700">Veg</span>
      </div>
    </div>
  );

  const Footer = () => (
    <div className="flex items-center justify-center gap-1.5 px-4 py-5 text-center text-[11px] text-slate-400">
      <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
      Payments secured by Razorpay
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-[#520400]" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-sm w-full bg-white rounded-2xl border border-slate-200 shadow-lg p-6 text-center space-y-2">
          <h1 className="text-lg font-extrabold text-slate-900">Payment link not found</h1>
          <p className="text-sm text-slate-500">This link isn't valid. Please check the link and try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Header storeName={data.storeName} storeLogo={data.storeLogo} />

      <div className="mx-auto w-full max-w-md flex-1 px-4 py-6 space-y-5">
        {result ? (
          result.outcome === "success" ? (
            <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-6 text-center space-y-3">
              <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
              <h1 className="text-lg font-extrabold text-emerald-800">Payment Received — Delivery Booked!</h1>
              <p className="text-sm text-emerald-700">
                Thank you! Your payment went through and a rider will be assigned shortly.
              </p>
              <a
                href={result.trackingUrl}
                className="mt-2 inline-flex h-11 w-full items-center justify-center rounded-xl bg-emerald-600 text-sm font-bold text-white shadow-sm transition-colors hover:bg-emerald-700"
              >
                Track Your Delivery
              </a>
            </div>
          ) : result.outcome === "order_failed" ? (
            <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-6 text-center space-y-2">
              <PackageX className="mx-auto h-10 w-10 text-amber-600" />
              <h1 className="text-lg font-extrabold text-amber-800">We Couldn't Complete Your Order</h1>
              <p className="text-sm text-amber-700">{result.reason}, so we're unable to fulfil this order.</p>
              <p className="text-sm font-semibold text-amber-800">
                {result.refunded
                  ? "Your payment has been refunded and should reflect in your account shortly."
                  : "Our team has been notified to process your refund shortly."}
              </p>
            </div>
          ) : result.outcome === "already_failed" ? (
            <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-6 text-center space-y-2">
              <PackageX className="mx-auto h-10 w-10 text-amber-600" />
              <h1 className="text-lg font-extrabold text-amber-800">This Order Couldn't Be Completed</h1>
              <p className="text-sm text-amber-700">{result.reason}</p>
            </div>
          ) : result.outcome === "already_processed" ? (
            <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-6 text-center space-y-2">
              <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
              <h1 className="text-lg font-extrabold text-emerald-800">Payment Already Processed</h1>
              <p className="text-sm text-emerald-700">This order has already been paid and confirmed.</p>
            </div>
          ) : (
            <div className="rounded-2xl border-2 border-red-200 bg-red-50 p-6 text-center space-y-2">
              <XCircle className="mx-auto h-10 w-10 text-red-600" />
              <h1 className="text-lg font-extrabold text-red-800">Something Went Wrong</h1>
              <p className="text-sm text-red-700">
                We couldn't confirm your order automatically. Please contact your seller — your payment is safe and
                will be reviewed shortly.
              </p>
            </div>
          )
        ) : processing ? (
          <div className="rounded-2xl border-2 border-slate-100 bg-white p-8 text-center space-y-3">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-[#520400]" />
            <p className="text-sm font-semibold text-slate-700">Confirming your payment and booking delivery…</p>
            <p className="text-xs text-slate-400">This can take a few seconds. Please don't close this page.</p>
          </div>
        ) : data.status === "paid" ? (
          <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-6 text-center space-y-2">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
            <h1 className="text-lg font-extrabold text-emerald-800">Payment Already Completed</h1>
            <p className="text-sm text-emerald-700">This order has already been paid. No further action is needed.</p>
          </div>
        ) : data.status === "failed" ? (
          <div className="rounded-2xl border-2 border-red-200 bg-red-50 p-6 text-center space-y-2">
            <XCircle className="mx-auto h-10 w-10 text-red-600" />
            <h1 className="text-lg font-extrabold text-red-800">Payment Could Not Be Completed</h1>
            <p className="text-sm text-red-700">{data.failureReason || "This checkout could not be completed."}</p>
          </div>
        ) : data.isExpired ? (
          <div className="rounded-2xl border-2 border-slate-200 bg-white p-6 text-center space-y-2">
            <Clock className="mx-auto h-10 w-10 text-slate-400" />
            <h1 className="text-lg font-extrabold text-slate-800">Payment Link Expired</h1>
            <p className="text-sm text-slate-500">Please ask your seller to share a new payment link.</p>
          </div>
        ) : !data.razorpayConnected ? (
          <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-6 text-center space-y-2">
            <AlertTriangle className="mx-auto h-10 w-10 text-amber-600" />
            <h1 className="text-lg font-extrabold text-amber-800">Online Payment Not Available</h1>
            <p className="text-sm text-amber-700">
              This store hasn't set up online payment yet. Please contact your seller to arrange payment another way.
            </p>
          </div>
        ) : (
          <>
            <div className="rounded-2xl border-2 border-slate-100 bg-white shadow-sm overflow-hidden">
              {PAYMENT_STATUS_META[data.status] && (
                <div className="border-b border-slate-100 px-5 py-3">
                  {(() => {
                    const meta = PAYMENT_STATUS_META[data.status];
                    const StatusIcon = meta.Icon;
                    return (
                      <span className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold ${meta.cls}`}>
                        <StatusIcon className="h-3.5 w-3.5" /> {meta.label}
                      </span>
                    );
                  })()}
                </div>
              )}

              <div className="p-5">
                <h1 className="text-sm font-bold text-slate-900 mb-4">Complete Your Payment</h1>

                {Array.isArray(data.items) && data.items.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {data.items.map((item, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="min-w-0 text-slate-700">
                          {item.name} <span className="font-bold text-slate-900">× {item.quantity || 1}</span>
                          {item.variantLabel && (
                            <span className="block text-xs font-bold text-slate-900">{item.variantLabel}</span>
                          )}
                        </span>
                        <span className="shrink-0 text-slate-900 font-medium">
                          ₹{(Number(item.price) * Number(item.quantity || 1)).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="space-y-2 border-t border-slate-100 pt-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Subtotal</span>
                    <div className="flex items-center gap-6">
                      <span className="text-xs text-slate-400">
                        {data.itemCount} {data.itemCount === 1 ? "item" : "items"}
                      </span>
                      <span className="font-medium text-slate-900">₹{Number(data.subtotal).toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Shipping</span>
                    <div className="flex items-center gap-6">
                      <span className="text-xs text-slate-400">{data.fulfillmentMethod}</span>
                      <span className="font-medium text-slate-900">
                        {data.freeDelivery ? (
                          <>
                            <span className="mr-1 text-slate-400 line-through">₹{Number(data.shippingFee).toFixed(2)}</span>
                            Free
                          </>
                        ) : Number(data.shippingFee) === 0 ? (
                          "Free"
                        ) : (
                          `₹${Number(data.shippingFee).toFixed(2)}`
                        )}
                      </span>
                    </div>
                  </div>
                  {data.gstAmount > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">
                        GST ({data.gstPercent}%){data.gstInclusive ? " (Including)" : ""}
                      </span>
                      <span className="font-medium text-slate-900">₹{Number(data.gstAmount).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between border-t border-slate-100 pt-2.5 font-semibold text-slate-900">
                    <span>Order Total</span>
                    <span className="text-right leading-tight">
                      <span className="block text-base font-bold">₹{Math.round(data.totalExact ?? data.amount)}</span>
                      <span className="block text-xs font-normal text-slate-400">
                        ₹{Number(data.totalExact ?? data.amount).toFixed(2)}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {feePrompt && (
              <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
                  <div className="text-sm text-amber-800">
                    <p className="font-bold">Your delivery fee has changed</p>
                    <p className="mt-1">
                      Previous: ₹{Number(feePrompt.previousFare).toFixed(2)} → New: ₹{Number(feePrompt.newFare).toFixed(2)}
                    </p>
                    <p className="mt-1 font-semibold">New order total: ₹{feePrompt.newTotal}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setFeePrompt(null);
                      load();
                    }}
                    className="h-10 flex-1 rounded-lg border border-amber-300 bg-white text-sm font-semibold text-amber-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handlePay}
                    className="h-10 flex-1 rounded-lg bg-amber-600 text-sm font-bold text-white"
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {payError && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-semibold text-red-700">
                {payError}
              </div>
            )}

            {!feePrompt && (
              <button
                type="button"
                onClick={handlePay}
                disabled={paying}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#520400] text-sm font-bold text-white shadow-sm transition-colors hover:bg-[#3A0300] disabled:opacity-60"
              >
                {paying ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Processing…
                  </>
                ) : (
                  `Pay ₹${Number(data.amount).toFixed(2)}`
                )}
              </button>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}
